//this page will handle admin login

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  TextField,
  InputAdornment,
  IconButton,
  Link,
  Fade,
  LinearProgress,
  Stack,
  Alert,
  // useTheme,
  // useMediaQuery,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import { 
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowBack as BackIcon,
  // AdminPanelSettings as AdminIcon,
  // Person as PersonIcon,
  ArrowForward as ArrowIcon,
  Google as GoogleIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

// Import your components
import api from '../../api'
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import AuthStepper from '../../components/AuthStepper';
import '../../styles/AdminLogin.css';

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username_or_email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mounted, setMounted] = useState(false);
  
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    if (loginError) setLoginError('');
  };

  const validateForm = () => {
    const newErrors = {};
    // Validate username_or_email (accepts either email or username) and password
    if (!formData.username_or_email || formData.username_or_email.trim() === '') {
      newErrors.username_or_email = 'Email or username is required';
    } else {
      // If it looks like an email, validate email format
      const value = formData.username_or_email.trim();
      if (value.includes('@') && !/\S+@\S+\.\S+/.test(value)) {
        newErrors.username_or_email = 'Please enter a valid email address';
      }
    }

    if (!formData.password || formData.password.trim() === '') {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const payloadBase = {
        username_or_email: formData.username_or_email.trim(),
        password: formData.password.trim(),
      };

      // Some databases use role 'admin', others 'college_admin'.
      // Try 'admin' first (matches UI wording), then fall back.
      let response;
      try {
        response = await api.post('users/login/', { ...payloadBase, role: 'admin' });
      } catch (e1) {
        response = await api.post('users/login/', { ...payloadBase, role: 'college_admin' });
      }

      // if (response?.access) {
      //   try {
      //     localStorage.setItem('access_token', response.access);
      //     if (response.refresh) localStorage.setItem('refresh_token', response.refresh);
      //   } catch {}
      // }

      window.location.replace('/admin/dashboard');
    } catch (error) {
      const backendDetail = error?.response?.data?.detail;
      const backendMessage = error?.response?.data?.message;
      const backendNonField = Array.isArray(error?.response?.data?.non_field_errors)
        ? error.response.data.non_field_errors[0]
        : null;

      setLoginError(
        backendDetail || backendMessage || backendNonField ||
        error?.message ||
        'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  if (!mounted) return null;

  return (
    <Box 
      className="admin-login-background"
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #374151 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '-10%',
          width: { xs: '300px', md: '400px' },
          height: { xs: '300px', md: '400px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(75, 85, 99, 0.15) 0%, rgba(55, 65, 81, 0.08) 70%, transparent 100%)',
          animation: 'float 6s ease-in-out infinite',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '-5%',
          width: { xs: '250px', md: '350px' },
          height: { xs: '250px', md: '350px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(55, 65, 81, 0.12) 0%, rgba(75, 85, 99, 0.06) 70%, transparent 100%)',
          animation: 'float 8s ease-in-out infinite reverse',
          zIndex: 0
        }}
      />
      
      {/* Navbar */}
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <Navbar />
      </Box>
      
      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        pt: 4,
        pb: 8,
        position: 'relative',
        zIndex: 1
      }}>
        <Fade in={mounted} timeout={800}>
          <AuthStepper
            currentStep={2}
            totalSteps={2}
            palette={{
              barGradient: 'linear-gradient(90deg, #4b5563 0%, #6b7280 100%)',
              barShadow: '0 0 10px rgba(75, 85, 99, 0.5)',
              trackColor: 'rgba(75, 85, 99, 0.3)',
              dotGradient: 'linear-gradient(135deg, #4b5563, #6b7280)',
              dotShadow: '0 2px 8px rgba(75, 85, 99, 0.6)',
            }}
            containerProps={{ maxWidth: 'md', sx: { mb: 4 } }}
            paperProps={{
              elevation: 0,
              sx: {
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(75, 85, 99, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              },
            }}
            dotProps={{
              sx: {
                position: 'absolute',
                top: -2,
                right: -1,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4b5563, #6b7280)',
                boxShadow: '0 2px 8px rgba(75, 85, 99, 0.6)',
                animation: 'pulse 2s infinite',
              },
            }}
            leftSlot={(
              <Link
                component="button"
                onClick={() => window.history.back()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  color: '#9ca3af',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#6b7280',
                    transform: 'translateX(-2px)',
                  },
                }}
              >
                <BackIcon sx={{ fontSize: 18 }} />
                Back to Role Selection
              </Link>
            )}
            rightSlot={(
              <>
                <Chip
                  icon={<CheckIcon />}
                  label="Step 2 of 2"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #4b5563, #374151)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: 'white',
                    },
                  }}
                />
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                  Admin Login
                </Typography>
              </>
            )}
          />
        </Fade>

        <Container maxWidth="sm">
          <Fade in={mounted} timeout={1200}>
            <Card
              className="admin-login-card"
              sx={{
                borderRadius: 5,
                background: 'rgba(30, 41, 59, 0.98)',
                backdropFilter: 'blur(30px)',
                border: '1px solid rgba(75, 85, 99, 0.3)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
                overflow: 'visible',
                position: 'relative',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.5), 0 20px 25px -5px rgba(75, 85, 99, 0.3)'
                }
              }}
            >
              {/* Loading Progress Bar */}
              {loading && (
                <LinearProgress 
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    borderRadius: '20px 20px 0 0',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #4b5563 0%, #6b7280 100%)',
                      animation: 'shimmer 1s ease-in-out infinite alternate'
                    }
                  }}
                />
              )}

              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: { xs: 72, md: 88 },
                        height: { xs: 72, md: 88 },
                        background: 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)',
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        boxShadow: '0 8px 32px rgba(75, 85, 99, 0.5)',
                        animation: 'avatarFloat 3s ease-in-out infinite'
                      }}
                    >
                      👨‍💼
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)'
                      }}
                    >
                      <SecurityIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                  
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #f8fafc 0%, #6b7280 80%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      letterSpacing: '-0.02em',
                      animation: 'textShine 3s ease-in-out infinite'
                    }}
                  >
                    Admin Access
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#9ca3af',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 500,
                      maxWidth: 400,
                      mx: 'auto',
                      lineHeight: 1.6
                    }}
                  >
                    Secure login to access administrative controls and platform oversight tools.
                  </Typography>
                </Box>

                {/* Error Alert */}
                {loginError && (
                  <Fade in={!!loginError}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3,
                        borderRadius: 3,
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                        border: '1px solid rgba(220, 38, 38, 0.3)',
                        color: '#fca5a5',
                        '& .MuiAlert-icon': {
                          fontSize: 20,
                          color: '#fca5a5'
                        },
                        '& .MuiAlert-message': {
                          fontWeight: 500
                        }
                      }}
                    >
                      {loginError}
                    </Alert>
                  </Fade>
                )}

                {/* Login Form */}
                <Box component="form" onSubmit={handleSubmit} noValidate className="admin-login-form">
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Admin Email"
                      type="email"
                      value={formData.username_or_email}
                      onChange={handleInputChange('username_or_email')}
                      error={!!errors.username_or_email}
                      helperText={errors.username_or_email}
                      placeholder="admin@peercampus.com"
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        sx: {
                          color: '#f8fafc',
                        },
                      }}
                      InputLabelProps={{
                        style: { color: '#9ca3af' }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          backgroundColor: 'rgba(55, 65, 81, 0.8)',
                          color: '#f8fafc',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '& fieldset': {
                            borderColor: 'rgba(75, 85, 99, 0.5)'
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(55, 65, 81, 0.9)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(75, 85, 99, 0.3)',
                            '& fieldset': {
                              borderColor: 'rgba(107, 114, 128, 0.7)'
                            }
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'rgba(55, 65, 81, 1)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 20px rgba(75, 85, 99, 0.4)',
                            '& fieldset': {
                              borderColor: '#6b7280'
                            }
                          },
                          height: { xs: 40, md: 'auto' },
                          display: 'flex',
                          alignItems: 'center'
                        },
                        '& .MuiInputBase-input': {
                          padding: { xs: '8px 12px', md: '12px 14px' },
                          height: '100%',
                          boxSizing: 'border-box'
                        }
                      }}
                    />


                    <TextField
                      fullWidth
                      label="Admin Password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      error={!!errors.password}
                      helperText={errors.password}
                      placeholder="Enter your secure password"
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                              sx={{
                                color: '#9ca3af',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(75, 85, 99, 0.3)',
                                  color: '#f8fafc'
                                }
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        style: { color: '#9ca3af' }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          backgroundColor: 'rgba(55, 65, 81, 0.8)',
                          color: '#f8fafc',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '& fieldset': {
                            borderColor: 'rgba(75, 85, 99, 0.5)'
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(55, 65, 81, 0.9)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(75, 85, 99, 0.3)',
                            '& fieldset': {
                              borderColor: 'rgba(107, 114, 128, 0.7)'
                            }
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'rgba(55, 65, 81, 1)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 20px rgba(75, 85, 99, 0.4)',
                            '& fieldset': {
                              borderColor: '#6b7280'
                            }
                          },
                          height: { xs: 40, md: 'auto' },
                          display: 'flex',
                          alignItems: 'center'
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#fca5a5'
                        },
                        '& .MuiInputBase-input': {
                          padding: { xs: '8px 12px', md: '12px 14px' },
                          height: '100%',
                          boxSizing: 'border-box'
                        }
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      endIcon={loading ? null : <ArrowIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)',
                        color: 'white',
                        // slightly larger than inputs on mobile; reduced on desktop
                        py: { xs: '12px', md: '18px' },
                        px: { xs: '18px', md: 4 },
                        borderRadius: { xs: 3, md: 4 },
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: { xs: '0.98rem', md: '1.05rem' },
                        boxShadow: '0 8px 25px rgba(75, 85, 99, 0.6)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: { xs: 52, md: 44 },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 30px rgba(75, 85, 99, 0.7)',
                        },
                        '&:disabled': {
                          background: 'rgba(75, 85, 99, 0.6)',
                          transform: 'none',
                          boxShadow: '0 4px 12px rgba(75, 85, 99, 0.4)',
                          animation: loading ? 'buttonPulse 1.5s ease-in-out infinite' : 'none'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover::before': {
                          left: '100%'
                        }
                      }}
                    >
                      {loading ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              borderTopColor: 'white',
                              animation: 'spin 1s linear infinite'
                            }}
                          />
                          <span>Authenticating...</span>
                        </Stack>
                      ) : (
                        'Access Admin Panel'
                      )}
                    </Button>
                  </Stack>
                </Box>

                {/* Security Notice */}
                <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 1, 
                      mb: 2,
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: '1px solid rgba(75, 85, 99, 0.3)'
                    }}
                  >
                    <SecurityIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ 
                      color: '#fbbf24', 
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}>
                      Secure Administrative Access
                    </Typography>
                  </Box>
                  <Link
                    href="/admin/forgot-password"
                    sx={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#d1d5db'
                      }
                    }}
                  >
                    Forgot admin password?
                  </Link>
                </Box>

                {/* Divider */}
                <Divider sx={{ my: 4, borderColor: 'rgba(75, 85, 99, 0.3)' }}>
                  <Typography variant="body2" sx={{ 
                    color: '#9ca3af', 
                    px: 3,
                    fontWeight: 500,
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    borderRadius: 2
                  }}>
                    Or use SSO
                  </Typography>
                </Divider>

                {/* Social Login */}
                <Stack direction="column" spacing={2} sx={{ mb: 4 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={loading}
                    startIcon={<GoogleIcon />}
                    onClick={() => handleSocialLogin('google')}
                    className="social-login-button"
                    sx={{
                      borderColor: 'rgba(75, 85, 99, 0.5)',
                      color: '#9ca3af',
                      borderRadius: 4,
                      py: 2.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#6b7280',
                        color: '#f8fafc',
                        backgroundColor: 'rgba(55, 65, 81, 0.8)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                      }
                    }}
                  >
                    Google SSO
                  </Button>
                </Stack>

                
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default AdminLogin;